import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
    explanation: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const EnvironmentIntroduction: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showExplanation, setShowExplanation] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {
        setShowExplanation(false);
        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeSocialStudies/EnvironmentIntroduction.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeSocialStudies/EnvironmentIntroduction.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setShowExplanation(false);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
        else {
            setShowExplanation(true);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "The Environment – An Introduction | Class 3 Social Studies (CBSE & ICSE)";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Learn the meaning of the environment, types of pollution, and ways to protect nature in this Class 3 Social Studies chapter. Includes tips on cleanliness and plantation for CBSE & ICSE students." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <header>
                                <h1 className="mb-5">Chapter 7: The Environment: An Introduction</h1>
                            </header>

                            <section>
                                <h2>Meaning of Environment</h2>
                                <p>The environment is everything around us — the air, water, land, plants, animals, and even people. It includes both <strong>natural things</strong> like forests, rivers, and mountains, and <strong>human-made things</strong> like houses, roads, and bridges. The environment provides us with all the resources we need to live.</p>
                            </section>

                            <section>
                                <h2>Pollution – An Introduction</h2>
                                <p>Pollution happens when harmful substances are added to the environment, making it dirty and unsafe for living things. It affects plants, animals, and humans. Pollution can damage health, spoil natural beauty, and disturb the balance of nature.</p>
                            </section>

                            <section>
                                <h2>Types of Pollution</h2>
                                <ul>
                                    <li><strong>Air Pollution</strong> – Caused by smoke from vehicles, factories, and burning waste. It makes the air dirty and can cause breathing problems.</li>
                                    <li><strong>Water Pollution</strong> – Caused when waste, chemicals, and plastics are thrown into rivers, lakes, and oceans. It makes the water unsafe to drink and harms aquatic life.</li>
                                    <li><strong>Noise Pollution</strong> – Caused by loud sounds from traffic, machines, and loudspeakers. It can cause stress, headaches, and hearing problems.</li>
                                </ul>
                            </section>

                            <section>
                                <h2>Plantation to Save the Environment – A Case Study</h2>
                                <div className="highlight">
                                    <p>Planting trees is one of the best ways to save the environment. Trees clean the air, give us oxygen, provide shade, and reduce heat. In one school project, students planted 100 trees in their neighborhood. This made the area greener, cleaner, and cooler, and birds started coming back. It showed that even small actions can make a big difference.</p>
                                </div>
                            </section>

                            <section>
                                <h2>Need for Cleanliness in the Surroundings</h2>
                                <p>Keeping our surroundings clean is important for:</p>
                                <ul>
                                    <li>Good health and hygiene</li>
                                    <li>Preventing diseases caused by germs and pests</li>
                                    <li>Making the environment pleasant and beautiful</li>
                                </ul>
                                <p>We can keep our surroundings clean by:</p>
                                <ul>
                                    <li>Throwing waste in dustbins</li>
                                    <li>Not littering public places</li>
                                    <li>Participating in cleanliness drives</li>
                                </ul>
                            </section>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <header>
                            <h1>Class 3 Social Studies – The Environment: An Introduction</h1>
                        </header>

                        <section>
                            <h2>Meaning of Environment</h2>
                            <p>The environment is everything around us — the air, water, land, plants, animals, and even people. It includes both <strong>natural things</strong> like forests, rivers, and mountains, and <strong>human-made things</strong> like houses, roads, and bridges. The environment provides us with all the resources we need to live.</p>
                        </section>

                        <section>
                            <h2>Pollution – An Introduction</h2>
                            <p>Pollution happens when harmful substances are added to the environment, making it dirty and unsafe for living things. It affects plants, animals, and humans. Pollution can damage health, spoil natural beauty, and disturb the balance of nature.</p>
                        </section>

                        <section>
                            <h2>Types of Pollution</h2>
                            <ul>
                                <li><strong>Air Pollution</strong> – Caused by smoke from vehicles, factories, and burning waste. It makes the air dirty and can cause breathing problems.</li>
                                <li><strong>Water Pollution</strong> – Caused when waste, chemicals, and plastics are thrown into rivers, lakes, and oceans. It makes the water unsafe to drink and harms aquatic life.</li>
                                <li><strong>Noise Pollution</strong> – Caused by loud sounds from traffic, machines, and loudspeakers. It can cause stress, headaches, and hearing problems.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>Plantation to Save the Environment – A Case Study</h2>
                            <div className="highlight">
                                <p>Planting trees is one of the best ways to save the environment. Trees clean the air, give us oxygen, provide shade, and reduce heat. In one school project, students planted 100 trees in their neighborhood. This made the area greener, cleaner, and cooler, and birds started coming back. It showed that even small actions can make a big difference.</p>
                            </div>
                        </section>

                        <section>
                            <h2>Need for Cleanliness in the Surroundings</h2>
                            <p>Keeping our surroundings clean is important for:</p>
                            <ul>
                                <li>Good health and hygiene</li>
                                <li>Preventing diseases caused by germs and pests</li>
                                <li>Making the environment pleasant and beautiful</li>
                            </ul>
                            <p>We can keep our surroundings clean by:</p>
                            <ul>
                                <li>Throwing waste in dustbins</li>
                                <li>Not littering public places</li>
                                <li>Participating in cleanliness drives</li>
                            </ul>
                        </section>

                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}

                            {(showExplanation) ? (
                                <>
                                    <span><strong>Correct option:</strong> {currentQuestion.answer}</span>
                                    <div dangerouslySetInnerHTML={{ __html: currentQuestion.explanation }}>
                                    </div>
                                    <div className="mt-3 text-center">
                                        <button className="btn hero-btn" onClick={loadRandomSyllabusQuestions}>Next Question</button>
                                    </div>
                                </>
                            ) : (<></>)}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default EnvironmentIntroduction;
